import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Upload, X, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ImageUpload = ({ imageType, currentImage, onImageUploaded, label, aspectRatio = 'auto' }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentImage);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('image_type', imageType);

      const response = await axios.post(
        `${BACKEND_URL}/api/upload/image`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const imageUrl = response.data.image_url;
      setPreview(imageUrl);
      onImageUploaded(imageUrl);
      toast.success('Image uploaded and compressed!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview(null);
    onImageUploaded(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const getAspectClass = () => {
    if (aspectRatio === 'square') return 'aspect-square';
    if (aspectRatio === 'cover') return 'aspect-[3/1]';
    return 'aspect-video';
  };

  return (
    <div className="space-y-2" data-testid={`image-upload-${imageType}`}>
      {label && <label className="text-sm font-medium text-white block">{label}</label>}
      
      <div className="relative">
        {preview ? (
          <div className="relative group">
            <img src={preview} alt={label} className={`w-full ${getAspectClass()} object-cover rounded-xl border border-white/10`} />
            <button onClick={handleRemove} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-destructive flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid="remove-image-button">
              <X className="w-4 h-4 text-white" />
            </button>
          </div>
        ) : (
          <div onClick={() => fileInputRef.current?.click()} className={`w-full ${getAspectClass()} border-2 border-dashed border-white/20 rounded-xl flex flex-col items-center justify-center gap-3 cursor-pointer hover:border-primary/50 transition-all bg-white/5`} data-testid="upload-dropzone">
            {uploading ? (
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            ) : (
              <>
                <Upload className="w-8 h-8 text-muted-foreground" />
                <div className="text-center">
                  <p className="text-sm text-white">Click to upload</p>
                  <p className="text-xs text-muted-foreground mt-1">Auto-compressed for faster loading</p>
                </div>
              </>
            )}
          </div>
        )}
        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" data-testid="file-input" />
      </div>
    </div>
  );
};

export default ImageUpload;